import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Alert from "../../components/common/Alert";
import ActionButton from "../../components/common/ActionButton";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import FilterBar, {
  formatDateInputValue,
} from "../../components/common/FilterBar";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import {
  useGetCompletedOrdersQuery,
} from "../../store/api";
import {
  formatCurrency,
  formatDateTime,
  getDefaultReportingFilters,
  getUniqueCurrencyCode,
  getPresetDateRange,
} from "../../utils/reporting";
import { downloadExcelFile } from "../../utils/excelDownload";

const parseCsvParam = (searchParams, key) =>
  searchParams
    .getAll(key)
    .flatMap((value) => String(value).split(","))
    .map((value) => value.trim())
    .filter(Boolean);

const getCompletedOrderFiltersFromSearchParams = (searchParams) => {
  const defaults = getDefaultReportingFilters();
  const page = Number(searchParams.get("page"));
  const pageSize = Number(searchParams.get("pageSize"));

  return {
    ...defaults,
    storeIds: parseCsvParam(searchParams, "storeIds"),
    from: searchParams.get("from") || defaults.from,
    to: searchParams.get("to") || defaults.to,
    channels: parseCsvParam(searchParams, "channels"),
    paymentStatuses: parseCsvParam(searchParams, "paymentStatuses"),
    search: searchParams.get("search") || "",
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 20,
  };
};

const buildCompletedOrderSearchParams = (filters) => {
  const defaults = getDefaultReportingFilters();
  const params = new URLSearchParams();

  if (filters.storeIds?.length) {
    filters.storeIds.forEach((storeId) => {
      params.append("storeIds", storeId);
    });
  }

  if (filters.from && filters.from !== defaults.from) {
    params.set("from", filters.from);
  }

  if (filters.to && filters.to !== defaults.to) {
    params.set("to", filters.to);
  }

  if (Array.isArray(filters.channels) && filters.channels.length > 0) {
    filters.channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  if (
    Array.isArray(filters.paymentStatuses) &&
    filters.paymentStatuses.length > 0
  ) {
    filters.paymentStatuses.forEach((status) => {
      params.append("paymentStatuses", status);
    });
  }

  if (filters.search) {
    params.set("search", filters.search);
  }

  if (filters.page && filters.page !== 1) {
    params.set("page", String(filters.page));
  }

  if (filters.pageSize && filters.pageSize !== 20) {
    params.set("pageSize", String(filters.pageSize));
  }

  return params;
};

const CompletedOrders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = useSelector((state) => state.auth.token);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const [draftFilters, setDraftFilters] = useState(() =>
    getCompletedOrderFiltersFromSearchParams(searchParams),
  );
  const [appliedFilters, setAppliedFilters] = useState(() =>
    getCompletedOrderFiltersFromSearchParams(searchParams),
  );

  const queryArgs = useMemo(() => {
    return {
      storeIds: appliedFilters.storeIds,
      from: appliedFilters.from,
      to: appliedFilters.to,
      channels: appliedFilters.channels,
      paymentStatuses: appliedFilters.paymentStatuses,
      search: appliedFilters.search,
      page: appliedFilters.page,
      pageSize: appliedFilters.pageSize,
    };
  }, [appliedFilters]);

  const { data, isLoading, isFetching, isError, error } =
    useGetCompletedOrdersQuery(queryArgs);

  const orders = Array.isArray(data?.orders) ? data.orders : [];
  const page = data?.page || appliedFilters.page;
  const pageSize = data?.pageSize || appliedFilters.pageSize;
  const totalCount = data?.totalCount || 0;
  const currencyCode = getUniqueCurrencyCode(orders);
  const periodGrandTotal = Number(data?.totalSale || 0);
  const periodTotalRefund = Number(data?.totalRefund || 0);
  const paymentStatusFilter =
    draftFilters.paymentStatuses[0] || "";
  const paymentStatusOptions = [
    { value: "", label: "All" },
    { value: "CAPTURED", label: "Captured" },
    { value: "REFUNDED", label: "Refunded" },
  ];

  const applyFilters = (nextFilters) => {
    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setSearchParams(buildCompletedOrderSearchParams(nextFilters), {
      replace: true,
    });
  };

  const handleViewOrder = (order) => {
    const orderRef =
      order?.edgeOrderId ||
      order?.orderId ||
      order?.orderNo ||
      `${order?.storeId || "order"}-${order?.orderNo || "detail"}`;

    navigate(
      {
        pathname: `/reporting/completed-orders/${encodeURIComponent(orderRef)}`,
        search: location.search,
      },
      {
        state: { order },
      },
    );
  };

  const columns = [
    { key: "orderNo", label: "ORDER ID" },
    { key: "placedAt", label: "PLACED AT" },
    { key: "store", label: "STORE" },
    { key: "status", label: "PAYMENT STATUS" },
    { key: "grandTotal", label: "GRAND TOTAL" },
    { key: "actions", label: "ACTIONS" },
  ];

  const rows = orders.map((order) => ({
    id: order.edgeOrderId || `${order.storeId}-${order.orderNo}`,
    orderNo: order.orderNo || order.edgeOrderId || "-",
    placedAt: formatDateTime(order.placedAt || order.completedAt),
    store: order.storeName || "-",
    status: order.paymentStatus || order.status || "-",
    grandTotal: formatCurrency(
      order.grandTotal,
      order.currencyCode || currencyCode,
    ),
    actions: (
      <ActionButton
        label="View"
        icon="view"
        onClick={() => handleViewOrder(order)}
      />
    ),
  }));

  const resetFilters = () => {
    const next = {
      ...getDefaultReportingFilters(),
      channels: [],
      paymentStatuses: [],
      search: "",
      page: 1,
      pageSize: 20,
    };
    applyFilters(next);
  };
  const datePresets = [
    { value: "today", label: "Today" },
    { value: "last7", label: "Last 7 Days" },
    { value: "last30", label: "Last 30 Days" },
  ];
  const activePreset =
    datePresets.find((preset) => {
      const range = getPresetDateRange(preset.value);
      return draftFilters.from === range.from && draftFilters.to === range.to;
    })?.value || "";

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      setDownloadError("");
      await downloadExcelFile({
        endpoint: "/admin/reporting/completed-orders/export",
        queryParams: {
          storeIds: appliedFilters.storeIds,
          from: appliedFilters.from,
          to: appliedFilters.to,
          channels: appliedFilters.channels,
          paymentStatuses: appliedFilters.paymentStatuses,
          search: appliedFilters.search,
        },
        token,
        fallbackFileName: "central-completed-orders.xlsx",
      });
    } catch (err) {
      if (err?.status === 401) {
        setDownloadError("Session expired. Please log in again.");
        navigate("/login");
      } else if (err?.isInvalidExcelResponse) {
        setDownloadError(
          "Export response is not an Excel file. Please verify API base URL or proxy configuration.",
        );
      } else {
        setDownloadError("Failed to download Excel. Please try again.");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Completed Orders"
        subtitle="Historical completed-order history across selected Central stores."
        actions={(
          <Button
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            className="h-9 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-[0_8px_16px_rgba(79,70,229,0.28)] hover:bg-indigo-700"
          >
            {isDownloading ? "Downloading..." : "Download"}
          </Button>
        )}
      />

      <Card>
        <div className="border-b border-slate-200 pb-6 mb-6">
          <FilterBar
            startDate={draftFilters.from}
            endDate={draftFilters.to}
            onStartDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                from: value ? formatDateInputValue(value) : "",
                page: 1,
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onEndDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                to: value ? formatDateInputValue(value) : "",
                page: 1,
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            showStore={false}
            presets={datePresets}
            activePreset={activePreset}
            onPresetChange={(presetKey) => {
              const range = getPresetDateRange(presetKey);
              const nextFilters = {
                ...draftFilters,
                from: range.from,
                to: range.to,
                page: 1,
              };
              applyFilters(nextFilters);
            }}
            showSearch
            search={draftFilters.search || ""}
            onSearchChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                search: value,
                page: 1,
              };
              applyFilters(nextFilters);
            }}
            searchPlaceholder="Search orders..."
            showStatus
            statusLabel="Payment Status"
            status={paymentStatusFilter}
            statusOptions={paymentStatusOptions}
            onStatusChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                paymentStatuses: value ? [value] : [],
                page: 1,
              };
              applyFilters(nextFilters);
            }}
            onReset={resetFilters}
            isLoading={isFetching}
          />
          {downloadError ? (
            <div className="mt-4">
              <Alert tone="error">{downloadError}</Alert>
            </div>
          ) : null}

          <div className="mt-6 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Orders</h3>
              <p className="text-sm text-slate-500">
                Showing {rows.length} of {totalCount}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2">
                <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide">
                  TOTAL SALES
                </div>
                <div className="text-xl font-bold text-emerald-900">
                  {formatCurrency(periodGrandTotal, currencyCode)}
                </div>
              </div>
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2">
                <div className="text-xs font-medium text-rose-700 uppercase tracking-wide">
                  TOTAL REFUND
                </div>
                <div className="text-xl font-bold text-rose-900">
                  {formatCurrency(periodTotalRefund, currencyCode)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {isError && (
          <div className="mt-6">
            <Alert tone="error">
              {error?.data?.detail || "Failed to load completed orders."}
            </Alert>
          </div>
        )}

        <div className="mt-6">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((value) => (
                <div
                  key={value}
                  className="h-12 animate-pulse rounded-lg bg-slate-100"
                />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <Alert tone="info">No orders found for selected filters.</Alert>
          ) : (
            <Table columns={columns} rows={rows} />
          )}
        </div>

          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            isLoading={isFetching}
            onPageChange={(nextPage) => {
            applyFilters({ ...draftFilters, page: nextPage });
            }}
            onPageSizeChange={(nextPageSize) => {
            applyFilters({
              ...draftFilters,
              page: 1,
              pageSize: nextPageSize,
            });
            }}
          />
      </Card>
    </div>
  );
};

export default CompletedOrders;
